import { ownerBrandForPath } from '~/config/route-brand.config';
import { buildCrossBrandUrl, getBrandUrl, type Brand } from '~/lib/brand';

/**
 * Resolve an internal app path to the correct link for the brand the caller
 * is currently rendering on.
 *
 * The single funnel every cross-surface internal link should go through.
 * Given an arbitrary path and the current brand, it returns either:
 *
 * - a **relative** path (unchanged) when the path's owning brand is the
 *   current brand or `'both'` — keeps same-surface client navigation fast and
 *   avoids needless absolute URLs; or
 * - an **absolute** URL on the owning brand's domain when the path belongs to
 *   the *other* brand — so a link rendered on Flow to a Ballee-only surface
 *   (e.g. a competition apply page) points at `ballee.co`, not the current
 *   host.
 *
 * Query strings and hashes are preserved end-to-end: `ownerBrandForPath`
 * strips them only to decide ownership, and `buildCrossBrandUrl` concatenates
 * the full path (including `?...`/`#...`) onto the brand base URL. So
 * `resolveAppUrl('/competitions/x/apply?applicationId=y', 'flow')` →
 * `https://ballee.co/competitions/x/apply?applicationId=y`.
 *
 * In dev/preview there is only one origin (no two-domain split), so
 * `buildCrossBrandUrl` yields an absolute URL on the same origin — correct,
 * just not visibly cross-domain. Cross-brand absolute URLs are only
 * meaningfully verifiable on staging/prod.
 *
 * @param path     An internal app path, optionally with `?query`/`#hash`.
 * @param currentBrand The brand the caller is rendering on (from the
 *   `x-brand` header via `getBrand()` on the server or `useBrand()` on the
 *   client).
 */
export function resolveAppUrl(path: string, currentBrand: Brand): string {
  const owner = ownerBrandForPath(path);

  // Same surface (or shared) → relative path, no rewrite.
  if (owner === 'both' || owner === currentBrand) {
    return path;
  }

  // Cross-brand → absolute URL on the owning brand's domain. `owner` is
  // narrowed to a concrete Brand here (it is neither 'both' nor currentBrand).
  return buildCrossBrandUrl(owner, path);
}

/**
 * The on-current-origin endpoint that mints a cross-brand auth handoff. Lives
 * on the CURRENT brand's host (it reads the current session cookie), so it is
 * always referenced relatively.
 */
const HANDOFF_ENDPOINT = '/api/cross-brand/handoff';

/**
 * Returns `true` when following `path` is a *real* Flow ↔ Ballee hop — i.e.
 * the path is owned by a different brand AND that brand resolves to a
 * different origin than `currentBrand`.
 *
 * `'both'` and same-brand paths are never cross-brand. The origin check is
 * what makes this safe in **dev/preview**: there both brands resolve to the
 * same origin (`NEXT_PUBLIC_SITE_URL`), so a different-owner path is still
 * same-origin and must NOT detour through the cross-brand handoff — that
 * detour would point at the shared prod-ish host and drop the local session,
 * stranding the user (the GDO post-signup dead-end). Only on staging/prod,
 * where Flow and Ballee resolve to distinct hosts, is it a true hop.
 */
export function isCrossBrand(path: string, currentBrand: Brand): boolean {
  const owner = ownerBrandForPath(path);
  if (owner === 'both' || owner === currentBrand) {
    return false;
  }
  // Different owner brand — but only a real hop when the origins differ.
  return getBrandUrl(owner) !== getBrandUrl(currentBrand);
}

/**
 * Resolve an internal path to the href a *navigation* link should use,
 * routing cross-brand hops through the authenticated handoff endpoint.
 *
 * This is the funnel `<BrandLink>` and server-rendered nav links use so a
 * logged-in user crossing Flow ↔ Ballee stays signed in (the handoff mints a
 * one-time magic link). It builds on {@link resolveAppUrl}:
 *
 * - **Same brand / `'both'`** → the relative path, unchanged (no hop, no
 *   handoff — fast client nav, valid cookie).
 * - **Cross-brand** → a relative URL to the handoff endpoint on the *current*
 *   origin: `/api/cross-brand/handoff?to=<absolute dest URL>`. The handoff
 *   route is the single decision point: it passes anonymous users straight
 *   through to the bare destination, short-circuits same-brand, and only
 *   mints for an authenticated cross-brand hop. So callers do NOT need to know
 *   the auth state — they always point cross-brand nav at the handoff, and the
 *   endpoint does the right thing.
 *
 * The `to` param carries the **absolute** destination URL (from
 * `resolveAppUrl`) so the handoff's host allow-list is exercised and the
 * destination brand is unambiguous on the server.
 *
 * @param path         An internal app path, optionally with `?query`/`#hash`.
 * @param currentBrand The brand the caller renders on.
 */
export function resolveCrossBrandHref(
  path: string,
  currentBrand: Brand,
): string {
  if (!isCrossBrand(path, currentBrand)) {
    // Same surface / shared → relative path, no handoff.
    return path;
  }

  // Cross-brand → route through the handoff endpoint on the current origin,
  // carrying the absolute destination URL as `to`.
  const absoluteDest = resolveAppUrl(path, currentBrand);
  return `${HANDOFF_ENDPOINT}?to=${encodeURIComponent(absoluteDest)}`;
}
